import {useEffect, useMemo, useState} from 'react';
import {useImmer} from 'use-immer';
import {useLocation} from 'react-router-dom';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import {Box} from '@mui/material';
import theme from './theme';
import styles from './styles';
import './styles.scss';
import Header from './components/Header';
import SwitchActiveLayoutView from './components/SwitchActiveView';
import ChoicesConfirmationView from './views/ChoicesConfirmationView';
import TransitionView from './views/TransitionView';
import AppSummaryView from './views/AppSummaryView';
import Loading from '~/components/Loading';

import {BookingData, BookingView} from './types';
import {
  isChoicesConfirmationViewChoice,
  isAppSummaryViewChoice,
  handleBeforeUnload,
} from './utils';
import useAuthLoad from '../../hooks/useAuthLoad';

function BookingScreen() {
  const {isLoading} = useAuthLoad();

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  if (isLoading) return <Loading />;

  return (
    <ThemeProvider theme={theme}>
      <BookingScreenContent />
    </ThemeProvider>
  );
}

function BookingScreenContent() {
  const location = useLocation();
  const [bookingData, setBookingData] = useImmer<BookingData>({});

  const initialView = useMemo(() => {
    return location.pathname === '/booking/paypal-return'
      ? BookingView.PayPalReturn
      : BookingView.TransitionView;
  }, [location.pathname]);

  const [activeView, setActiveView] = useState(initialView);

  const renderMainView = () => {
    if (activeView === BookingView.TransitionView) {
      return <TransitionView setActiveView={setActiveView} />;
    }
    if (
      activeView === BookingView.ChoicesConfirmationView &&
      isChoicesConfirmationViewChoice(bookingData)
    ) {
      return (
        <ChoicesConfirmationView
          bookingChoices={bookingData}
          setActiveView={setActiveView}
          setBookingChoices={setBookingData}
        />
      );
    }
    if (
      activeView === BookingView.AppSummaryView &&
      isAppSummaryViewChoice(bookingData)
    ) {
      return (
        <AppSummaryView
          bookingChoices={bookingData}
          setActiveView={setActiveView}
          setBookingData={setBookingData}
        />
      );
    }
    return (
      <Box sx={styles.wrapper}>
        <Header setBookingData={setBookingData} setActiveView={setActiveView} />
        <SwitchActiveLayoutView
          bookingData={bookingData}
          setBookingData={setBookingData}
          activeView={activeView}
          setActiveView={setActiveView}
        />
      </Box>
    );
  };

  return renderMainView();
}

export default BookingScreen;
